import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { userAPI, authAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import './Profile.css';

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [tab, setTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    phone: user.phone || '',
    businessName: user.businessName || '',
    businessDescription: user.businessDescription || '',
    businessAddress: user.businessAddress || '',
    city: user.city || '',
    state: user.state || '',
    country: user.country || '',
    zipCode: user.zipCode || '',
  });
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const handlePwChange = e => setPwForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleProfileSave = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      await userAPI.updateProfile(form);
      await refreshUser();
      toast.success('Profile updated!');
    } catch (err) {
      toast.error(err.response?.data?.error?.message || 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async e => {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirmPassword) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await authAPI.changePassword({ currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
      toast.success('Password changed!');
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast.error(err.response?.data?.error?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async e => {
    if (!e.target.files?.[0]) return;
    const fd = new FormData();
    fd.append('avatar', e.target.files[0]);
    try {
      await userAPI.uploadAvatar(fd);
      await refreshUser();
      toast.success('Avatar updated!');
    } catch { toast.error('Upload failed'); }
  };

  const initials = `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase();

  return (
    <div className="profile-page page">
      <div className="container">
        <h1 className="mb-3">My Profile</h1>

        <div className="profile-layout">
          {/* Sidebar */}
          <aside className="profile-sidebar">
            <div className="profile-avatar-section">
              <div className="avatar avatar-xl">
                {user.avatar
                  ? <img src={`http://localhost:5000${user.avatar}`} alt={user.firstName} />
                  : initials
                }
              </div>
              <div>
                <div className="font-bold">{user.firstName} {user.lastName}</div>
                <div className="text-light text-sm">{user.email}</div>
                <span className="badge badge-primary mt-1">{user.role}</span>
              </div>
            </div>
            <label className="btn btn-ghost btn-sm btn-block" style={{ cursor: 'pointer' }}>
              📷 Change Photo
              <input type="file" accept="image/*" onChange={handleAvatarUpload} style={{ display: 'none' }} />
            </label>
          </aside>

          {/* Main */}
          <div className="profile-main">
            <div className="tabs">
              <button className={`tab ${tab === 'profile' ? 'active' : ''}`} onClick={() => setTab('profile')}>Profile Info</button>
              <button className={`tab ${tab === 'password' ? 'active' : ''}`} onClick={() => setTab('password')}>Security</button>
            </div>

            {tab === 'profile' && (
              <form onSubmit={handleProfileSave} className="profile-form">
                <div className="form-section">
                  <h3>Personal Information</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">First Name</label>
                      <input type="text" name="firstName" className="form-input" value={form.firstName} onChange={handleChange} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Last Name</label>
                      <input type="text" name="lastName" className="form-input" value={form.lastName} onChange={handleChange} required />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input type="email" className="form-input" value={user.email} disabled style={{ opacity: 0.6 }} />
                    <span className="form-hint">Email cannot be changed here.</span>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input type="tel" name="phone" className="form-input" value={form.phone} onChange={handleChange} placeholder="+1 (555) 000-0000" />
                  </div>
                </div>

                {user.role === 'SELLER' && (
                  <div className="form-section">
                    <h3>Business Details</h3>
                    <div className="form-group">
                      <label className="form-label">Business Name</label>
                      <input type="text" name="businessName" className="form-input" value={form.businessName} onChange={handleChange} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Business Description</label>
                      <textarea name="businessDescription" className="form-textarea" value={form.businessDescription} onChange={handleChange} rows={3} />
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">City</label>
                        <input type="text" name="city" className="form-input" value={form.city} onChange={handleChange} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">State</label>
                        <input type="text" name="state" className="form-input" value={form.state} onChange={handleChange} />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Country</label>
                        <input type="text" name="country" className="form-input" value={form.country} onChange={handleChange} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">ZIP Code</label>
                        <input type="text" name="zipCode" className="form-input" value={form.zipCode} onChange={handleChange} />
                      </div>
                    </div>
                  </div>
                )}

                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </form>
            )}

            {tab === 'password' && (
              <form onSubmit={handlePasswordChange} className="profile-form">
                <div className="form-section">
                  <h3>Change Password</h3>
                  <div className="form-group">
                    <label className="form-label">Current Password</label>
                    <input type="password" name="currentPassword" className="form-input" value={pwForm.currentPassword} onChange={handlePwChange} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">New Password</label>
                    <input type="password" name="newPassword" className="form-input" value={pwForm.newPassword} onChange={handlePwChange} required minLength={8} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Confirm New Password</label>
                    <input type="password" name="confirmPassword" className="form-input" value={pwForm.confirmPassword} onChange={handlePwChange} required />
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Changing...' : 'Change Password'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
